import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';
import { Users, Plus, MapPin, Search, Eye, ClipboardList, Calendar } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';

const fmt = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const CentersPage = ({ lookupMode = false }) => {
  const [centers, setCenters] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [showMeeting, setShowMeeting] = useState(false);
  const [showSheet, setShowSheet] = useState(null);
  const [search, setSearch] = useState('');
  const [form, setForm] = useState({ name: '', office_id: '', meeting_day: '', meeting_time: '', meeting_location: '', activation_date: '' });
  const [meetingForm, setMeetingForm] = useState({ date: new Date().toISOString().split('T')[0], notes: '', total_collected: '' });

  const load = useCallback(async () => {
    if (lookupMode && !search) { setCenters([]); setLoading(false); return; }
    setLoading(true);
    try {
      const params = search ? `?q=${encodeURIComponent(search)}` : '';
      const [cRes, oRes] = await Promise.all([api.get(`/centers${params}`), api.get('/offices').catch(() => ({ data: [] }))]);
      setCenters(cRes.data);
      setOffices(oRes.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [search, lookupMode]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e) => { e.preventDefault(); load(); };

  const handleSubmit = async () => {
    try {
      await api.post('/centers', form);
      toast.success('Center created');
      setShowModal(false);
      setForm({ name: '', office_id: '', meeting_day: '', meeting_time: '', meeting_location: '', activation_date: '' });
      load();
    } catch (e) { toast.error('Failed to create center'); }
  };

  const openDetail = async (centerId) => {
    try {
      const res = await api.get(`/centers/${centerId}`);
      setDetailData(res.data);
      setShowDetail(centerId);
    } catch { toast.error('Failed to load center'); }
  };

  const handleRecordMeeting = async () => {
    try {
      await api.post(`/centers/${showDetail}/meetings`, meetingForm);
      toast.success('Meeting recorded');
      setShowMeeting(false);
      openDetail(showDetail);
    } catch { toast.error('Failed to record meeting'); }
  };

  const openCollectionSheet = async (centerId) => {
    try {
      const res = await api.get(`/centers/${centerId}/collection-sheet`);
      setShowSheet(res.data);
    } catch { toast.error('Failed to generate collection sheet'); }
  };

  const getOfficeName = (id) => offices.find(o => o.id === id)?.name || '-';

  return (
    <div className="space-y-6 fade-in" data-testid="centers-page">
      <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
        <span>Clients</span><span>/</span><span className="text-[#1f1f1f] font-medium">Centers</span>
      </div>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C5A059] mb-1">GROUP OPERATIONS</p>
          <h1 className="font-display text-2xl font-bold text-[#1f1f1f]">Centers</h1>
          {lookupMode && <p className="text-sm text-gray-500 mt-1">Search by center name</p>}
        </div>
        <Button onClick={() => setShowModal(true)} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="add-center-btn">
          <Plus className="w-4 h-4 mr-2" /> Create Center
        </Button>
      </div>

      <form onSubmit={handleSearch} className="flex gap-3">
        <div className="relative flex-1 max-w-xl">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by center name..." className="pl-9 text-sm" data-testid="center-search" />
        </div>
        <Button type="submit" className="bg-[#1f1f1f] text-white text-sm px-6">Search</Button>
      </form>

      {lookupMode && !search && centers.length === 0 ? (
        <div className="text-center py-16">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Search for a center by name to view details</p>
        </div>
      ) : (
      <div className="bg-white border border-gray-200">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Center</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Number</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Office</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Meeting Day</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Groups</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
            <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            centers.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-gray-400">{search ? 'No centers match' : 'No centers found'}</td></tr> :
            centers.map(c => (
              <tr key={c.id} className="border-b hover:bg-gray-50" data-testid={`center-row-${c.id}`}>
                <td className="px-4 py-3 font-medium text-sm text-[#1f1f1f]">{c.name}</td>
                <td className="px-4 py-3 text-sm text-gray-600 font-mono">{c.center_number || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{getOfficeName(c.office_id)}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{c.meeting_day || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{c.group_count || 0}</td>
                <td className="px-4 py-3"><span className="text-xs px-2 py-1 rounded bg-green-50 text-green-700">{c.status || 'Active'}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openDetail(c.id)} data-testid={`center-detail-${c.id}`}><Eye className="w-3 h-3 mr-1" />View</Button>
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openCollectionSheet(c.id)} data-testid={`center-sheet-${c.id}`}><ClipboardList className="w-3 h-3 mr-1" />Sheet</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* Create Center Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" aria-describedby="center-form-desc">
          <DialogHeader><DialogTitle>Create Center</DialogTitle></DialogHeader>
          <p id="center-form-desc" className="sr-only">Create a new center</p>
          <div className="space-y-4">
            <div><Label>Center Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="center-name" /></div>
            <div><Label>Office *</Label>
              <Select value={form.office_id} onValueChange={v => setForm({...form, office_id: v})}>
                <SelectTrigger data-testid="center-office"><SelectValue placeholder="Select Office" /></SelectTrigger>
                <SelectContent>{offices.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Meeting Day</Label>
                <Select value={form.meeting_day} onValueChange={v => setForm({...form, meeting_day: v})}>
                  <SelectTrigger><SelectValue placeholder="Select Day" /></SelectTrigger>
                  <SelectContent>{['Monday','Tuesday','Wednesday','Thursday','Friday','Saturday','Sunday'].map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Meeting Time</Label><Input type="time" value={form.meeting_time} onChange={e => setForm({...form, meeting_time: e.target.value})} /></div>
            </div>
            <div><Label>Meeting Location</Label><Input value={form.meeting_location} onChange={e => setForm({...form, meeting_location: e.target.value})} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="center-submit">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Center Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby="center-detail-desc">
          <DialogHeader><DialogTitle>{detailData?.name} — {detailData?.center_number}</DialogTitle></DialogHeader>
          <p id="center-detail-desc" className="sr-only">Center details</p>
          {detailData && (
            <div className="space-y-5">
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div><span className="text-gray-500">Office:</span> {detailData.office_name || '-'}</div>
                <div><span className="text-gray-500">Meeting:</span> {detailData.meeting_day || '-'} {detailData.meeting_time || ''}</div>
                <div><span className="text-gray-500">Location:</span> {detailData.meeting_location || '-'}</div>
              </div>

              {/* Linked Groups */}
              <div>
                <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Linked Groups ({detailData.groups?.length || 0})</h4>
                <div className="border border-gray-200">
                  <table className="w-full text-xs">
                    <thead><tr className="bg-gray-50 border-b"><th className="text-left px-3 py-2">Group</th><th className="text-left px-3 py-2">Number</th><th className="text-right px-3 py-2">Members</th><th className="text-left px-3 py-2">Status</th></tr></thead>
                    <tbody>
                      {(detailData.groups || []).map(g => (
                        <tr key={g.id} className="border-b"><td className="px-3 py-2 font-medium">{g.name}</td><td className="px-3 py-2 font-mono">{g.group_number || '-'}</td><td className="px-3 py-2 text-right">{g.member_count}</td><td className="px-3 py-2 capitalize">{g.status}</td></tr>
                      ))}
                      {(!detailData.groups || detailData.groups.length === 0) && <tr><td colSpan={4} className="text-center py-3 text-gray-400">No groups linked. Assign center_id when creating groups.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Meetings */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">Meeting History</h4>
                  <Button size="sm" variant="outline" className="text-xs h-7" onClick={() => setShowMeeting(true)} data-testid="record-meeting-btn"><Calendar className="w-3 h-3 mr-1" />Record Meeting</Button>
                </div>
                {detailData.meetings?.length > 0 ? (
                  <div className="border border-gray-200 max-h-40 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-gray-50 border-b"><th className="text-left px-3 py-2">Date</th><th className="text-right px-3 py-2">Collected</th><th className="text-left px-3 py-2">Notes</th></tr></thead>
                      <tbody>{detailData.meetings.map((m, i) => (<tr key={i} className="border-b"><td className="px-3 py-1.5">{m.date}</td><td className="px-3 py-1.5 text-right font-mono">{fmt(m.total_collected)}</td><td className="px-3 py-1.5">{m.notes || '-'}</td></tr>))}</tbody>
                    </table>
                  </div>
                ) : <p className="text-xs text-gray-400">No meetings recorded</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Record Meeting Dialog */}
      <Dialog open={showMeeting} onOpenChange={setShowMeeting}>
        <DialogContent className="max-w-md" aria-describedby="meeting-desc">
          <DialogHeader><DialogTitle>Record Meeting</DialogTitle></DialogHeader>
          <p id="meeting-desc" className="sr-only">Record center meeting</p>
          <div className="space-y-4">
            <div><Label>Date *</Label><Input type="date" value={meetingForm.date} onChange={e => setMeetingForm({...meetingForm, date: e.target.value})} /></div>
            <div><Label>Total Collected (ZAR)</Label><Input type="number" step="0.01" value={meetingForm.total_collected} onChange={e => setMeetingForm({...meetingForm, total_collected: e.target.value})} /></div>
            <div><Label>Notes</Label><Textarea value={meetingForm.notes} onChange={e => setMeetingForm({...meetingForm, notes: e.target.value})} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMeeting(false)}>Cancel</Button>
            <Button onClick={handleRecordMeeting} className="bg-[#1f1f1f] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Collection Sheet Dialog */}
      <Dialog open={!!showSheet} onOpenChange={() => setShowSheet(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" aria-describedby="sheet-desc">
          <DialogHeader><DialogTitle>Collection Sheet — {showSheet?.center_name}</DialogTitle></DialogHeader>
          <p id="sheet-desc" className="sr-only">Collection sheet</p>
          {showSheet && (
            <div className="space-y-4">
              <p className="text-xs text-gray-500">Date: {showSheet.date} | Grand Total: <span className="font-bold text-[#1f1f1f]">{fmt(showSheet.grand_total)}</span></p>
              {showSheet.groups?.map((g, gi) => (
                <div key={gi} className="border border-gray-200">
                  <div className="bg-gray-50 px-3 py-2 flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#1f1f1f]">{g.group_name}</span>
                    <span className="text-xs font-mono">{fmt(g.group_total)}</span>
                  </div>
                  <table className="w-full text-xs">
                    <thead><tr className="border-b"><th className="text-left px-3 py-1.5">Member</th><th className="text-left px-3 py-1.5">Role</th><th className="text-right px-3 py-1.5">Loan Due</th><th className="text-right px-3 py-1.5">Savings</th><th className="text-right px-3 py-1.5 font-bold">Total</th></tr></thead>
                    <tbody>{g.members?.map((m, mi) => (<tr key={mi} className="border-b"><td className="px-3 py-1">{m.client_name}</td><td className="px-3 py-1 capitalize">{m.role}</td><td className="px-3 py-1 text-right font-mono">{fmt(m.loan_due)}</td><td className="px-3 py-1 text-right font-mono">{fmt(m.savings_expected)}</td><td className="px-3 py-1 text-right font-mono font-bold">{fmt(m.total_expected)}</td></tr>))}</tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CentersPage;
